import { PreconditionFailedError } from '../../../errors.js';
import type { CmmReadinessFacts } from '../../contracts/cmm-gateway.js';
import type {
  LineWorkflowReadiness,
  OrderReadiness,
  OrdersReadinessResponse,
} from '../../contracts/schemas/order-readiness.js';
import {
  CMM_READINESS_SEED,
  TRACING_READINESS_SEED,
} from '../../contracts/schemas/readiness.js';
import { loadCmmReadinessFactsForOrdersDb } from '../db/orders/cmm-readiness.js';
import {
  getSendReadinessLinesDb,
  getSendReadinessOrdersDb,
  type SendReadinessLineRow,
  type SendReadinessOrderRow,
} from '../db/orders/send-readiness.js';
import { toIso } from '../db/orders/transformers.js';
import { computeCmmReadiness } from './cmm-readiness.js';
import {
  collectDuplicateLineNumbers,
  lineNumberBlockedReasons,
} from './line-number-checks.js';
import {
  computeTracingReadiness,
  type TracingReadinessFacts,
} from './readiness.js';
import { readinessBlockedReasons } from './send-order-to-tracing.js';
import {
  type EmptyOrderVerdict,
  type LineSendVerdict,
  lineSendState,
  type OrderSendGate,
  orderSendState,
} from './send-state.js';
import type { UseCaseDeps } from './types.js';

/**
 * The send cell's state for every order on a page, for Tracing and CMM.
 *
 * Each line is judged with the rules its send applies — the same evaluators,
 * the same line-number checks, the same order-level refusals — so the cell
 * offers exactly what the send would take. A fixed number of queries per page,
 * one after another, whatever the page size.
 */

export interface GetOrdersReadinessParams {
  companyId: string;
  orderIds: string[];
  includeLines?: boolean;
  /** The CMM launch gate, resolved by the caller. Off means `cmm: null`. */
  includeCmm: boolean;
}

/** The send's own line-number reasons, after the seeded checks. */
const LINE_NUMBER_REASONS = [
  'missing_order_line_number',
  'duplicate_order_line_number',
  'order_line_missing',
];

const TRACING_KEY_ORDER = [
  ...TRACING_READINESS_SEED.map(entry => entry.key),
  ...LINE_NUMBER_REASONS,
];
const CMM_KEY_ORDER = [
  ...CMM_READINESS_SEED.map(entry => entry.key),
  ...LINE_NUMBER_REASONS,
];

const isParty = (order: SendReadinessOrderRow, companyId: string) =>
  companyId === order.buyerCompanyId || companyId === order.supplierCompanyId;

/** The refusals the Tracing send makes before it looks at a line. */
function tracingGate(
  order: SendReadinessOrderRow,
  companyId: string,
): OrderSendGate | null {
  if (!isParty(order, companyId)) return 'notApplicable';
  if (order.orderType === 'INTERNAL_ORDER') return 'internalOrder';
  if (order.isArchived) return 'archived';
  return null;
}

/** The refusals the CMM send makes before it looks at a line. */
function cmmGate(
  order: SendReadinessOrderRow,
  companyId: string,
): OrderSendGate | null {
  if (order.orderType !== 'PURCHASE_ORDER' || !isParty(order, companyId))
    return 'notApplicable';
  if (order.isArchived) return 'archived';
  return null;
}

const requiredKeys = (
  checks: { key: string; severity: string; status: string }[],
  match: (status: string) => boolean,
) =>
  checks
    .filter(check => check.severity === 'required' && match(check.status))
    .map(check => check.key);

/**
 * Tracing refuses `unknown` as well as `missing`: a check it could not
 * evaluate is not a check that passed.
 */
function tracingVerdicts(
  facts: TracingReadinessFacts | undefined,
  lines: SendReadinessLineRow[],
): { lines: LineSendVerdict[]; empty: EmptyOrderVerdict } {
  // Tracing checks duplicates among the lines it is sending.
  const duplicates = collectDuplicateLineNumbers(
    lines.filter(line => !line.tracingReflectionOrderId),
  );

  return {
    lines: lines.map(line => {
      // Sent wins over referenced, as in the send.
      if (line.tracingReflectionOrderId)
        return {
          sent: true,
          sentAt: toIso(line.tracingOrderRequestedAt),
          reasons: [],
          unknown: [],
        };
      if (line.referencedTracingOrderId)
        return {
          sent: true,
          referenced: true,
          sentAt: null,
          reasons: [],
          unknown: [],
        };
      return {
        sent: false,
        sentAt: null,
        reasons: [
          ...(facts
            ? readinessBlockedReasons(facts, line)
            : ['order_line_missing']),
          ...lineNumberBlockedReasons(line, duplicates),
        ],
        unknown: [],
      };
    }),
    empty: {
      missing: facts
        ? requiredKeys(
            computeTracingReadiness(facts.order, [], facts.ctx).checks,
            status => status !== 'met',
          )
        : [],
      unknown: [],
    },
  };
}

/**
 * CMM leaves `unknown` out of what blocks, the way its send does: an
 * unreadable supplier profile must not refuse every line.
 */
function cmmVerdicts(
  facts: CmmReadinessFacts,
  lines: SendReadinessLineRow[],
): { lines: LineSendVerdict[]; empty: EmptyOrderVerdict } {
  const readiness = computeCmmReadiness(facts);
  const byLineId = new Map(
    readiness.lines.map(line => [line.orderLineId, line]),
  );
  // CMM checks duplicates across every line: a re-send extends one record.
  const duplicates = collectDuplicateLineNumbers(lines);

  return {
    lines: lines.map(line => {
      const verdict = byLineId.get(line.id);
      if (line.cmmOrderLineId)
        return {
          sent: true,
          sentAt: toIso(line.cmmRequestedAt),
          reasons: [],
          unknown: [],
        };
      return {
        sent: false,
        sentAt: null,
        reasons: [
          ...(verdict ? verdict.missing : ['order_line_missing']),
          ...lineNumberBlockedReasons(line, duplicates),
        ],
        unknown: verdict
          ? requiredKeys(verdict.checks, status => status === 'unknown')
          : [],
      };
    }),
    empty: {
      missing: requiredKeys(readiness.checks, status => status === 'missing'),
      unknown: requiredKeys(readiness.checks, status => status === 'unknown'),
    },
  };
}

export const getOrdersReadiness = async (
  deps: UseCaseDeps,
  params: GetOrdersReadinessParams,
): Promise<OrdersReadinessResponse> => {
  const gateway = deps.tracingReadiness;
  if (!gateway) {
    // An unwired gateway would report every order ready.
    throw new PreconditionFailedError({
      code: 'readiness_unavailable',
      message: 'Tracing readiness could not be evaluated',
    });
  }

  const orderIds = [...new Set(params.orderIds)];
  const orders = await getSendReadinessOrdersDb(deps.db, {
    companyId: params.companyId,
    orderIds,
  });
  if (orders.length === 0) return {};

  const ownIds = orders.map(order => order.id);
  const lines = await getSendReadinessLinesDb(deps.db, ownIds);
  const tracingFacts = await gateway.loadFactsForOrders({
    companyId: params.companyId,
    orderIds: ownIds,
  });
  const cmmFacts = params.includeCmm
    ? await loadCmmReadinessFactsForOrdersDb(deps.db, {
        companyId: params.companyId,
        orderIds: ownIds,
      })
    : null;

  const result: OrdersReadinessResponse = {};
  for (const order of orders) {
    const orderLines = lines.filter(line => line.orderId === order.id);

    const tracingGateState = tracingGate(order, params.companyId);
    const tracing = tracingVerdicts(tracingFacts.get(order.id), orderLines);

    const orderCmmFacts = cmmFacts?.get(order.id);
    const cmmGateState = cmmGate(order, params.companyId);
    const cmm = orderCmmFacts ? cmmVerdicts(orderCmmFacts, orderLines) : null;

    const entry: OrderReadiness = {
      tracing: orderSendState({
        gate: tracingGateState,
        ...tracing,
        keyOrder: TRACING_KEY_ORDER,
      }),
      cmm: cmm
        ? orderSendState({
            gate: cmmGateState,
            ...cmm,
            keyOrder: CMM_KEY_ORDER,
          })
        : null,
    };

    if (params.includeLines) {
      entry.lines = Object.fromEntries(
        orderLines.map((line, index) => {
          const cmmLine: LineWorkflowReadiness | null = cmm
            ? lineSendState(cmmGateState, cmm.lines[index])
            : null;
          return [
            line.id,
            {
              tracing: lineSendState(tracingGateState, tracing.lines[index]),
              cmm: cmmLine,
            },
          ];
        }),
      );
    }

    result[order.id] = entry;
  }
  return result;
};
